import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { OnboardingStateAnswer } from '../../../../music24-api/src/trpc/onboarding.router';
import { useTRPCAPI, useTRPCClientAPI } from '../../trpc/trpc';
import { useUserToken } from '../../redux/slices/appInfo/hooks';
import { clearSignupIntent, markSignupIntentSent, readSignupIntent } from './signupIntent';

/**
 * After the sign-in, sends the stored signup intent once and clears it.
 *
 * The first shell after the sign-in is the one of Home, behind the welcome gate. It runs this hook
 * before the welcome page mounts, and `onboarding.state` can load before the server has the
 * intent. So the hook also keeps the intent for this page load (`markSignupIntentSent`, read
 * through `onboardingIntentOf`) and writes it into the cached answer at once. Then the welcome
 * headline and the tour order do not wait for the call. The server stores only the first intent,
 * so an answer that already has one is not changed.
 */
export function useSyncSignupIntent(): void {
  const trpcAPI = useTRPCAPI();
  const trpcClient = useTRPCClientAPI();
  const queryClient = useQueryClient();
  const token = useUserToken();

  useEffect(() => {
    if (token === null || token === '') {
      return;
    }
    const intent = readSignupIntent();
    if (intent === null) {
      return;
    }
    // Clear first so a re-render does not send the intent twice.
    clearSignupIntent();
    markSignupIntentSent(intent);
    const queryKey = trpcAPI.onboarding.state.queryKey();
    const patch = () =>
      queryClient.setQueryData(queryKey, (old: OnboardingStateAnswer | undefined) =>
        old === undefined || old.intent !== null ? old : { ...old, intent }
      );
    patch();
    trpcClient.onboarding.setIntent
      .mutate({ intent })
      .then(() => {
        // A read that started before the call can hold no intent: write it again, then refresh.
        patch();
        return queryClient.invalidateQueries({ queryKey });
      })
      .catch(console.error);
  }, [queryClient, token, trpcAPI, trpcClient]);
}
